import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { dashboardAPI } from '../../services/api';
import toast from 'react-hot-toast';

const fmt = (n) => Number(n || 0).toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 });

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    dashboardAPI.get()
      .then(res => setData(res.data))
      .catch(err => {
        const msg = err.response?.data?.error || err.message || 'Unknown error';
        console.error('Dashboard load error:', msg, err);
        toast.error(`Dashboard: ${msg}`);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="loading-container"><div className="spinner" /></div>;
  if (!data) return <div className="empty-state"><h3>No data available</h3></div>;

  const statusBadge = (status) => {
    const map = { paid: 'badge-green', pending: 'badge-yellow', partially_paid: 'badge-orange', draft: 'badge-gray', cancelled: 'badge-red', converted: 'badge-blue' };
    return <span className={`badge ${map[status] || 'badge-gray'}`}>{status?.replace('_', ' ')}</span>;
  };

  const trendData = (data.salesTrend || []).map(t => ({
    month: new Date(t.month).toLocaleDateString('en-IN', { month: 'short' }),
    sales: Number(t.sales),
    invoices: Number(t.invoice_count)
  }));

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Dashboard</h1>
          <p className="subtitle">Overview of your pharmaceutical business</p>
        </div>
        <div className="page-actions">
          <Link to="/invoices/new" className="btn btn-primary">+ New Invoice</Link>
        </div>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon green">₹</div>
          <div className="stat-info">
            <h3>Today's Sales</h3>
            <div className="stat-value">{fmt(data.today?.total_sales)}</div>
            <div className="stat-sub">{data.today?.invoice_count || 0} invoices</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon blue">₹</div>
          <div className="stat-info">
            <h3>This Month</h3>
            <div className="stat-value">{fmt(data.thisMonth?.total_sales)}</div>
            <div className="stat-sub">{data.thisMonth?.invoice_count || 0} invoices</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon orange">⚠</div>
          <div className="stat-info">
            <h3>Receivables</h3>
            <div className="stat-value">{fmt(data.receivables)}</div>
            <div className="stat-sub">Outstanding amount</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon red">↓</div>
          <div className="stat-info">
            <h3>Payables</h3>
            <div className="stat-value">{fmt(data.payables)}</div>
            <div className="stat-sub">Due to suppliers</div>
          </div>
        </div>
      </div>

      <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))' }}>
        <div className="stat-card" style={{ padding: 16 }}>
          <div className="stat-info">
            <h3>Products</h3>
            <div className="stat-value" style={{ fontSize: 20 }}>{data.products?.active_products || 0}</div>
          </div>
        </div>
        <Link to="/inventory/low-stock" className="stat-card" style={{ padding: 16, textDecoration: 'none', cursor: 'pointer' }}>
          <div className="stat-info">
            <h3 style={{ color: '#dc3545' }}>Low Stock</h3>
            <div className="stat-value text-danger" style={{ fontSize: 20 }}>{data.lowStockCount || 0}</div>
          </div>
        </Link>
        <Link to="/inventory/near-expiry" className="stat-card" style={{ padding: 16, textDecoration: 'none', cursor: 'pointer' }}>
          <div className="stat-info">
            <h3 style={{ color: '#e8590c' }}>Near Expiry</h3>
            <div className="stat-value text-warning" style={{ fontSize: 20 }}>{data.nearExpiryCount || 0}</div>
          </div>
        </Link>
        <div className="stat-card" style={{ padding: 16 }}>
          <div className="stat-info">
            <h3>Collected Today</h3>
            <div className="stat-value" style={{ fontSize: 20 }}>{fmt(data.today?.collected)}</div>
          </div>
        </div>
      </div>

      <div className="grid-2" style={{ marginTop: 24 }}>
        {/* Sales Trend Chart */}
        <div className="card">
          <div className="card-header"><h2>Sales Trend (6 Months)</h2></div>
          <div className="card-body" style={{ height: 280 }}>
            {trendData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={trendData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
                  <XAxis dataKey="month" fontSize={12} />
                  <YAxis fontSize={11} tickFormatter={v => `₹${(v/1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v) => fmt(v)} />
                  <Bar dataKey="sales" fill="#198754" radius={[4,4,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <div className="empty-state"><p>No sales data yet</p></div>}
          </div>
        </div>

        {/* Top Selling Products */}
        <div className="card">
          <div className="card-header"><h2>Top Selling Products</h2></div>
          <div className="card-body" style={{ padding: 0, maxHeight: 280, overflowY: 'auto' }}>
            {data.topProducts?.length > 0 ? (
              <table>
                <thead>
                  <tr><th>Product</th><th>Qty Sold</th><th style={{textAlign:'right'}}>Revenue</th></tr>
                </thead>
                <tbody>
                  {data.topProducts.map((p, i) => (
                    <tr key={i}>
                      <td><div style={{fontWeight:600}}>{p.name}</div><div className="text-xs text-secondary">{p.sku}</div></td>
                      <td className="td-mono">{p.qty_sold}</td>
                      <td className="td-amount">{fmt(p.revenue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <div className="empty-state" style={{ padding: 32 }}><p>No sales this month</p></div>}
          </div>
        </div>
      </div>

      {/* Recent Invoices */}
      <div className="card" style={{ marginTop: 24 }}>
        <div className="card-header">
          <h2>Recent Invoices</h2>
          <Link to="/invoices" className="btn btn-outline btn-sm">View All</Link>
        </div>
        <div className="table-container">
          <table>
            <thead>
              <tr><th>Invoice #</th><th>Type</th><th>Customer</th><th>Date</th><th style={{textAlign:'right'}}>Amount</th><th>Status</th></tr>
            </thead>
            <tbody>
              {(data.recentInvoices || []).map(inv => (
                <tr key={inv.id}>
                  <td><Link to={`/invoices/${inv.id}`} className="td-mono" style={{ color: 'var(--primary)', textDecoration: 'none', fontWeight: 600 }}>{inv.invoice_number}</Link></td>
                  <td><span className={`badge ${inv.invoice_type === 'gst_invoice' ? 'badge-green' : 'badge-blue'}`}>{inv.invoice_type === 'gst_invoice' ? 'GST' : 'Proforma'}</span></td>
                  <td>{inv.customer_name}</td>
                  <td className="text-secondary">{new Date(inv.invoice_date).toLocaleDateString('en-IN')}</td>
                  <td className="td-amount">{fmt(inv.total_amount)}</td>
                  <td>{statusBadge(inv.payment_status)}</td>
                </tr>
              ))}
              {(!data.recentInvoices || data.recentInvoices.length === 0) && (
                <tr><td colSpan={6} className="text-center text-secondary" style={{ padding: 32 }}>No invoices yet. <Link to="/invoices/new">Create your first invoice</Link></td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
